import type { ReactElement } from 'react';
import { Button } from '@/components/ui/button';

interface SimplePaginationProps {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

/**
 * Simple pagination component with "Showing X-Y of Z" text and Previous/Next buttons
 */
export function SimplePagination({
  page,
  limit,
  total,
  hasMore,
  onPrevious,
  onNext,
}: SimplePaginationProps): ReactElement | null {
  // Don't render if total items fit in one page
  if (total <= limit) {
    return null;
  }

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="flex justify-between items-center mt-4">
      <span className="text-sm text-muted-foreground">
        Showing {start}-{end} of {total}
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrevious}
          disabled={page === 1}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={!hasMore}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
